import type {
  ActivityStep,
  Conversation,
  Message,
  RetrievedChunkSnapshot,
  StoredDocument,
} from "../db/types";
import * as documents from "../db/documents";
import * as conversations from "../db/conversations";
import * as messages from "../db/messages";
import { getSetting, setSetting } from "../db/settings";
import { wipeDatabase } from "../db/schema";
import {
  ensurePersistentStorage,
  type StorageDurability,
} from "../db/persistence";
import type { Config } from "./config";
import { loadConfig, saveConfig, searchScope } from "./config";
import { tr } from "../i18n/i18n.svelte";
import { loadUser } from "../api/epflDirectory.svelte";
import {
  getAuthToken,
  getCurrentSession,
  hasSilentHint,
  loginEpfl,
  logoutEpfl,
  OidcError,
  onSessionChange,
  SessionExpiredError,
  silentLogin,
  silentLoginRedirect,
  type OidcSession,
} from "../api/auth";

export type View = "chat" | "docs" | "history";

const DEFAULT_CONVERSATION_TITLE = {
  fr: "Nouvelle conversation",
  en: "New conversation",
};

/** True while a conversation still carries the placeholder title it was
 *  created with — in either language, since the interface language may have
 *  changed since. The first answer then renames it after the question. */
export function isDefaultConversationTitle(title: string): boolean {
  return Object.values(DEFAULT_CONVERSATION_TITLE).includes(title);
}

/** Le titre tel qu'il s'affiche : le titre par défaut suit la langue de
 *  l'interface (une conversation créée en anglais ne reste pas « New
 *  conversation » dans une interface en français). */
export function shownTitle(title: string): string {
  return isDefaultConversationTitle(title) ? tr(DEFAULT_CONVERSATION_TITLE) : title;
}

// The working set — which documents are ticked, whether FullText is on — is
// kept across reloads. The other chat-bar toggles live in Config.
const ACTIVE_DOCS_KEY = "activeDocIds";
const BYPASS_RAG_KEY = "bypassRag";

export type UploadStatus =
  | "queued"
  | "parsing"
  | "done"
  | "error";

export interface UploadItem {
  id: string;
  filename: string;
  size: number;
  status: UploadStatus;
  /** Live progress label shown next to the filename while status === "parsing".
   *  Set from SSE events (per-page OCR, embedding batch, etc.) so the user can
   *  see the document isn't stuck. */
  detail?: string;
  error?: string;
  /** Structured progress for the v3 progress bar, alongside `detail`: share of
   *  the work done (0–1), pages read so far / in total (per-page counts only
   *  exist during OCR), whether OCR kicked in, and when parsing started — the
   *  basis of the time-remaining estimate. */
  fraction?: number;
  pagesDone?: number;
  pagesTotal?: number;
  ocr?: boolean;
  startedAt?: number;
  /** Joined from the v3 composer (paperclip, paste, drop): the question waits
   *  for it, unlike an upload from the Documents tab. */
  inChat?: boolean;
  /** The stored document, once read (the analysis dialog checks it). */
  docId?: string;
}

class AppState {
  /** Interface PolyChat (v3) : prompt en trois couches (socle, profil,
   *  consigne de l'usager) au lieu du prompt v1. Posé par src/v3/main.ts. */
  askEpfl = false;
  config = $state<Config | null>(null);
  configReady = $state(false);

  oidcSession = $state<OidcSession | null>(null);

  /** True while a no-UI re-login is in flight (startup or after an expired
   *  session). Lets the sign-in panel say "Reconnexion…" instead of flashing
   *  "not signed in" for the second or two the silent round-trip takes. */
  authReconnecting = $state(false);

  /** Durable-storage status (set non-blocking during init). null until resolved.
   *  Surfaced in the About page so the user knows whether their local library
   *  is protected from browser eviction (notably Safari's ~7-day ITP cap). */
  storage = $state<StorageDurability | null>(null);

  docs = $state<StoredDocument[]>([]);
  conversations = $state<Conversation[]>([]);

  /** User-created folder paths ("/"-separated), persisted in settings under
   *  "docFolders". Includes empty folders. The full set of folders shown in the
   *  UI is `allFolders()` — this list unioned with ancestors and any folder
   *  referenced by a document. */
  folders = $state<string[]>([]);

  /** Folder paths whose header checkbox is ticked in the doc library. Transient
   *  UI selection (not persisted) — lets the bulk bar delete folders, including
   *  empty ones that have no documents to select. */
  selectedFolders = $state<string[]>([]);

  currentConversationId = $state<string | null>(null);
  currentMessages = $state<Message[]>([]);

  activeDocIds = $state<string[]>([]);

  /** When true, the next user message bypasses the RAG pipeline (no embedding,
   *  no retrieval, no rerank) and ships the FULL parsed text of every active
   *  document to the LLM. Useful for cross-document comparison ("compare these
   *  20 contracts and tell me which is best"), where 10 retrieved chunks are
   *  not representative. Persisted with the active documents (settings, not
   *  Config): users expect a reload to keep the chat bar as they left it. */
  bypassRag = $state<boolean>(false);

  view = $state<View>("chat");
  showConfig = $state(false);
  // Set when the user clicks Retour while the config panel was forced open by
  // `needsConfig`. Lets them leave the panel (into a non-functional chat view)
  // instead of being trapped. Reset when they re-open config via the gear.
  configDismissed = $state(false);

  uploads = $state<UploadItem[]>([]);

  /** When the assistant is streaming a response, its current (partial) content lives here. */
  pendingAssistant = $state<string>("");
  /** Journal d'activité du tour en cours : reformulation, recherches (avec la
   *  requête exacte), réflexion du modèle. Persisté avec la réponse. */
  activity = $state<ActivityStep[]>([]);

  /** Ajoute une étape et renvoie son index, pour la compléter plus tard. */
  /** Signes de vie du tour en cours, pour l'usager qui attend : début du
   *  tour, dernier signe reçu (action, réflexion, texte) et volume de la
   *  réflexion — sans son contenu, qui n'est pas montré dans la v3. */
  turnStartedAt = $state(0);
  lastSignAt = $state(0);
  thinkingChars = $state(0);

  /** Un signe de vie du modèle ou d'un outil. */
  touch(): void {
    this.lastSignAt = Date.now();
  }

  addActivity(step: ActivityStep): number {
    this.activity.push(step);
    this.touch();
    return this.activity.length - 1;
  }

  /** La réflexion arrive par fragments : on prolonge la dernière étape de
   *  réflexion si elle est encore en cours, sinon on en ouvre une nouvelle
   *  (l'agent Ultra réfléchit entre deux recherches). */
  appendThinking(text: string): void {
    this.thinkingChars += text.length;
    this.touch();
    const last = this.activity[this.activity.length - 1];
    if (last?.kind === "thinking") last.text += text;
    else this.activity.push({ kind: "thinking", text });
  }
  pendingRetrieved = $state<Message["retrievedChunks"] | null>(null);
  isStreaming = $state(false);
  /** Coarse phase of the in-flight chat turn — drives the small status line
   *  shown to the user while waiting. Null when idle or actually streaming
   *  the answer. */
  retrievalPhase = $state<
    "embedding" | "searching" | "answering" | null
  >(null);
  lastError = $state<string | null>(null);
  /** Avertissement non bloquant (fichier produit mais incomplet), en orange. */
  lastWarning = $state<string | null>(null);

  /** AbortController for the in-flight chat turn. Non-reactive: UI watches isStreaming. */
  chatAbortController: AbortController | null = null;

  /** Tutorial popup (the "Aide" bubble in the composer toolbar): explains the
   *  source toggles, the Docs tab and the citations. */
  tutorialOpen = $state(false);
  /** Whether the first-run tutorial has already been shown. Defaults to `true`
   *  so nothing pops up before `init` has read the persisted flag. */
  tutorialSeen = $state(true);

  /** Show the tutorial once, on the first launch where the app is actually
   *  usable — opening it over the sign-in panel would just be in the way, so
   *  the caller decides when that is. Persists the flag immediately. */
  async openTutorialFirstRun(): Promise<void> {
    if (this.tutorialSeen) return;
    this.tutorialSeen = true;
    this.tutorialOpen = true;
    await setSetting("tutorialSeen", true);
  }

  /** Confidentiality warning shown the first time the user enables Exa web
   *  search (their query leaves EPFL for an external engine). `dismissed` is
   *  persisted via the settings store (decoupled from Config to avoid the
   *  ConfigView autosave clobbering it). */
  exaWebWarningOpen = $state(false);
  exaWebWarningDismissed = $state(false);

  /** Show the web-search confidentiality warning unless the user opted out. */
  openExaWebWarning(): void {
    if (!this.exaWebWarningDismissed) this.exaWebWarningOpen = true;
  }

  /** Close the warning. When `forever`, persist the opt-out so it never shows again. */
  async closeExaWebWarning(forever: boolean): Promise<void> {
    this.exaWebWarningOpen = false;
    if (forever) {
      this.exaWebWarningDismissed = true;
      await setSetting("exaWebWarningDismissed", true);
    }
  }

  /** Annonce du 24.09.2026 : un seul modèle (GLM-5.3-Flash) et un interrupteur
   *  Réflexion à la place du choix de modèle. Affichée à chaque démarrage tant
   *  que l'usager n'a pas coché « Ne plus afficher au démarrage » ; l'opt-out
   *  est persisté hors Config, comme celui de l'avertissement Exa. */
  modelNoticeOpen = $state(false);
  modelNoticeDismissed = $state(true);
  private modelNoticeShown = false;

  /** Une fois par session, et pas par-dessus le tutoriel du premier lancement :
   *  un nouvel usager n'a pas de « changement » à apprendre. */
  openModelNoticeOnStartup(): void {
    if (this.modelNoticeShown || this.modelNoticeDismissed || this.tutorialOpen) return;
    this.modelNoticeShown = true;
    this.modelNoticeOpen = true;
  }

  async closeModelNotice(dontShowAgain: boolean): Promise<void> {
    this.modelNoticeOpen = false;
    if (dontShowAgain) {
      this.modelNoticeDismissed = true;
      await setSetting("modelNoticeDismissed", true);
    }
  }

  /** True while the test-suite runner is iterating through questions. The
   *  individual chat turns each set/clear `isStreaming`; this flag stays true
   *  for the whole batch so the chat UI can show a "Running… X / Y" label. */
  testSuiteRunning = $state(false);
  /** 1-based index of the question currently being asked. 0 when idle. */
  testSuiteIndex = $state(0);
  /** Total number of questions in the running suite. 0 when idle. */
  testSuiteTotal = $state(0);
  /** Set by stopStreaming() when a suite is in flight, so the runner exits
   *  after the cancelled turn instead of moving to the next question. */
  testSuiteCancelled = $state(false);

  stopStreaming(): void {
    // Also mark the test-suite run as cancelled so the runner stops after the
    // current (aborted) turn instead of dispatching the next question.
    if (this.testSuiteRunning) this.testSuiteCancelled = true;
    this.chatAbortController?.abort();
  }

  /** Citation bubble overlay — set by clicking a [N] citation in an assistant message.
   *  `corpus` is the FULL set of retrieved chunks for the assistant turn that produced
   *  the citation, used as a fallback search space when the quote can't be matched in
   *  the cited chunk(s) (LLM occasionally confuses chunk numbers). Null when there's
   *  no quote to verify (e.g. clicking the "Passages consultés" list at the bottom of
   *  a message). */
  citationPreview = $state<{
    refs: RetrievedChunkSnapshot[];
    quote: string | null;
    corpus: RetrievedChunkSnapshot[] | null;
  } | null>(null);

  openCitation(
    refs: RetrievedChunkSnapshot[],
    quote: string | null = null,
    corpus: RetrievedChunkSnapshot[] | null = null,
  ): void {
    if (refs.length === 0) return;
    this.citationPreview = { refs, quote, corpus };
  }

  closeCitation(): void {
    this.citationPreview = null;
  }

  /** Whether the signed-in EPFL account is one of these addresses (case
   *  ignored). The email claim of the id_token, not `name`: matching the name
   *  claim missed Yannick's own account on 28.09, while the email is exactly
   *  what Config shows. Used to show a few settings to named people only —
   *  display, not a permission. */
  isSignedInAs(...emails: string[]): boolean {
    const email = this.oidcSession?.account.email.toLowerCase();
    return !!email && emails.includes(email);
  }

  get activeDocs(): StoredDocument[] {
    const set = new Set(this.activeDocIds);
    return this.docs.filter((d) => set.has(d.id));
  }

  /** Total character count of all chunks in active documents — used to show
   *  the user a context-window estimate when bypassRag is enabled. */
  get activeDocsCharCount(): number {
    let total = 0;
    for (const doc of this.activeDocs) {
      for (const chunk of doc.chunks) total += chunk.text.length;
    }
    return total;
  }

  async init(): Promise<void> {
    this.config = await loadConfig();
    this.oidcSession = await getCurrentSession();
    // Keep the badge honest when the session is renewed (or dropped) from deep
    // inside a chat turn rather than from a click here.
    onSessionChange((s) => {
      this.oidcSession = s;
      if (s) void loadUser(s.account.email).catch(() => {});
    });
    // Coordonnées EPFL (annuaire) pour les modèles Word et Excel, hors du
    // chemin critique : l'annuaire peut être lent.
    if (this.oidcSession) void loadUser(this.oidcSession.account.email).catch(() => {});
    this.folders = (await getSetting<string[]>("docFolders")) ?? [];
    this.exaWebWarningDismissed =
      (await getSetting<boolean>("exaWebWarningDismissed")) ?? false;
    this.tutorialSeen = (await getSetting<boolean>("tutorialSeen")) ?? false;
    this.modelNoticeDismissed =
      (await getSetting<boolean>("modelNoticeDismissed")) ?? false;
    // Stored session gone (Entra's 24h cap) but we know who was signed in:
    // reconnect silently. Off the critical path — the round-trip can hang on a
    // slow network, and the app must render meanwhile; `authReconnecting` holds
    // the sign-in panel back so it doesn't flash before the session lands.
    if (!this.oidcSession && (await hasSilentHint())) {
      this.authReconnecting = true;
      void this.trySilentSignIn();
    } else if (this.oidcSession && this.oidcSession.expiresAt <= Date.now()) {
      // Stored session with a stale id_token (every morning): find out NOW
      // whether its refresh token is still alive. Left to the first question,
      // a dead one surfaces mid-turn, where the only remaining fix — sending
      // the tab to Entra — would lose that question.
      this.authReconnecting = true;
      void this.warmUpSession();
    }
    this.configReady = true;
    // Request durable storage off the critical path: persist() may prompt
    // (Firefox) or be slow, and the app must render without waiting on it.
    void ensurePersistentStorage().then((s) => {
      this.storage = s;
    });
    await Promise.all([this.refreshDocs(), this.refreshConversations()]);
    await this.restoreWorkingSet();
  }

  /** Re-tick the documents and FullText toggle the user had before the reload,
   *  then keep them saved. Ids of documents deleted meanwhile (another tab, a
   *  bundle import) are dropped. A single effect watches the state rather than
   *  every mutation site — uploads, folder toggles and conversation loads all
   *  write `activeDocIds` directly. */
  private async restoreWorkingSet(): Promise<void> {
    const known = new Set(this.docs.map((d) => d.id));
    const saved = (await getSetting<string[]>(ACTIVE_DOCS_KEY)) ?? [];
    this.activeDocIds = saved.filter((id) => known.has(id));
    this.bypassRag = (await getSetting<boolean>(BYPASS_RAG_KEY)) ?? false;
    $effect.root(() => {
      $effect(() => {
        void setSetting(ACTIVE_DOCS_KEY, $state.snapshot(this.activeDocIds)).catch(() => {});
      });
      $effect(() => {
        void setSetting(BYPASS_RAG_KEY, this.bypassRag).catch(() => {});
      });
    });
  }

  /** Change some config fields and persist them. Saves a SNAPSHOT: spreading
   *  the $state proxy leaves nested arrays (testSuite) as proxies, which
   *  IndexedDB refuses to clone — the chat-bar toggles used to fail to save
   *  that way, silently, and came back off after every reload. */
  async updateConfig(patch: Partial<Config>): Promise<void> {
    if (!this.config) return;
    const next: Config = { ...$state.snapshot(this.config), ...patch };
    this.config = next;
    await saveConfig(next);
  }

  async loginEpfl(): Promise<void> {
    this.oidcSession = await loginEpfl();
  }

  /** Attempt an invisible re-login against the live Entra browser session.
   *  Returns false when Entra would need to show something — the caller then
   *  leaves the normal sign-in button to the user. */
  async trySilentSignIn(): Promise<boolean> {
    this.authReconnecting = true;
    let leaving = false;
    try {
      const session = await silentLogin();
      if (session) {
        this.oidcSession = session;
        return true;
      }
      // The hidden iframe got nothing (third-party cookies blocked, typically).
      // Nothing is in flight at startup, so let the tab itself go and ask:
      // true means the page is about to unload and come back signed in.
      leaving = await silentLoginRedirect();
      return leaving;
    } finally {
      // While the page unloads for Entra, keep the sign-in panel held back.
      if (!leaving) this.authReconnecting = false;
    }
  }

  /** Startup check of a stored-but-stale session: refresh it, and when the
   *  refresh token is dead (24h cap) and the iframe renewal fails too, fall
   *  back to the top-level renewal while nothing can be lost. Network trouble
   *  keeps the session: the first question simply retries. */
  private async warmUpSession(): Promise<void> {
    let leaving = false;
    try {
      await getAuthToken();
    } catch (e) {
      if (e instanceof SessionExpiredError) leaving = await silentLoginRedirect();
    }
    if (!leaving) this.authReconnecting = false;
  }

  /** Wipe ALL local data (documents, conversations, embeddings, config, OIDC
   *  session) and reload. Cross-browser replacement for the old "clear storage
   *  via chrome://settings" instructions, which only worked on Chrome. */
  async wipeAllData(): Promise<void> {
    await wipeDatabase();
    location.reload();
  }

  async logoutEpfl(): Promise<void> {
    await logoutEpfl();
    this.oidcSession = null;
  }

  /** Route a caught error to the UI. A dead OIDC session is surfaced as a clean
   *  re-login prompt: getAuthToken() already purged the stored session AND
   *  already tried to renew it silently, so reaching here means Entra genuinely
   *  wants the user. We mirror the purge into memory — which flips `needsConfig`
   *  and reopens the sign-in panel — and show a friendly message instead of a
   *  raw OAuth error. Everything else becomes lastError. */
  reportError(e: unknown): void {
    // Toute la famille OidcError, pas seulement l'expiration. Quelqu'un qui n'a
    // JAMAIS eu de session reçoit `OidcError("Not signed in to EPFL.")` : ce cas
    // n'était pas traité, donc le message brut s'affichait tel quel — en
    // anglais, et sans rouvrir le panneau de connexion, laissant l'usager devant
    // une erreur technique sans moyen d'agir. Deux situations distinctes, deux
    // phrases, même conséquence : on rouvre la connexion.
    if (e instanceof OidcError) {
      this.oidcSession = null;
      // Re-open the sign-in panel even if the user had dismissed config earlier.
      this.configDismissed = false;
      this.lastError =
        e instanceof SessionExpiredError
          ? tr({
              fr: "Votre session EPFL n'a pas pu être renouvelée automatiquement. Reconnectez-vous pour continuer.",
              en: "Your EPFL session could not be renewed automatically. Sign in again to continue.",
            })
          : tr({
              fr: "Connectez-vous avec votre compte EPFL pour poser une question.",
              en: "Sign in with your EPFL account to ask a question.",
            });
      return;
    }
    this.lastError = e instanceof Error ? e.message : String(e);
  }

  async refreshDocs(): Promise<void> {
    this.docs = await documents.listDocuments();
  }

  async refreshConversations(): Promise<void> {
    this.conversations = await conversations.listConversations();
  }

  async loadConversation(id: string): Promise<void> {
    const conv = await conversations.getConversation(id);
    if (!conv) return;
    this.currentConversationId = id;
    this.currentMessages = await messages.listMessagesByConversation(id);
    this.activeDocIds = [...conv.docIds];
    this.view = "chat";
  }

  async newConversation(docIds: string[] = this.activeDocIds): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await conversations.putConversation({
      id,
      title: tr(DEFAULT_CONVERSATION_TITLE),
      docIds: [...docIds],
      createdAt: now,
      updatedAt: now,
    });
    this.currentConversationId = id;
    this.currentMessages = [];
    this.activeDocIds = [...docIds];
    await this.refreshConversations();
    return id;
  }


  /** Persist the current active-doc set onto the open conversation so that
   *  re-opening it later re-checks exactly these documents. No-op when no
   *  conversation is open yet (the set is then captured at creation time by
   *  `newConversation`). `updatedAt` is intentionally left untouched so toggling
   *  documents doesn't reorder the history list. Best-effort / fire-and-forget. */
  private async syncConversationDocs(): Promise<void> {
    const convId = this.currentConversationId;
    if (!convId) return;
    try {
      const conv = await conversations.getConversation(convId);
      if (!conv) return;
      conv.docIds = [...this.activeDocIds];
      await conversations.putConversation(conv);
    } catch {
      // Non-critical UI state — ignore persistence hiccups.
    }
  }

  /** At each question: the documents queried (those added since, by upload,
   *  included) and the search scope become the conversation's own, restored
   *  when it is reopened. */
  async rememberConversationSettings(convId: string): Promise<void> {
    try {
      const conv = await conversations.getConversation(convId);
      if (!conv) return;
      conv.docIds = [...this.activeDocIds];
      if (this.config) conv.scope = searchScope(this.config);
      await conversations.putConversation(conv);
    } catch {
      // Non-critical UI state — ignore persistence hiccups.
    }
  }

  /** Replace the whole active-doc set (used by "select all / none") and persist
   *  it onto the current conversation. */
  setActiveDocs(ids: string[]): void {
    this.activeDocIds = [...ids];
    void this.syncConversationDocs();
  }

  toggleActiveDoc(docId: string): void {
    if (this.activeDocIds.includes(docId)) {
      this.activeDocIds = this.activeDocIds.filter((id) => id !== docId);
    } else {
      this.activeDocIds = [...this.activeDocIds, docId];
    }
    void this.syncConversationDocs();
  }

  async removeDoc(docId: string): Promise<void> {
    await documents.deleteDocument(docId);
    this.activeDocIds = this.activeDocIds.filter((id) => id !== docId);
    void this.syncConversationDocs();
    await this.refreshDocs();
  }

  async removeDocs(docIds: string[]): Promise<void> {
    if (docIds.length === 0) return;
    for (const id of docIds) {
      await documents.deleteDocument(id);
    }
    const drop = new Set(docIds);
    this.activeDocIds = this.activeDocIds.filter((id) => !drop.has(id));
    void this.syncConversationDocs();
    await this.refreshDocs();
  }

  // ---- Folders -------------------------------------------------------------

  private async persistFolders(): Promise<void> {
    await setSetting("docFolders", $state.snapshot(this.folders));
  }

  /** Normalize a folder path: trim each segment, drop empties, join with "/".
   *  Returns null for an effectively empty path (→ root). */
  private normalizeFolderPath(path: string): string | null {
    const segments = path
      .split("/")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return segments.length > 0 ? segments.join("/") : null;
  }

  /** All folder paths to display: the persisted list, every ancestor of those
   *  paths, and any folder referenced by a document. Sorted lexicographically
   *  so a parent always precedes its children. */
  get allFolders(): string[] {
    const set = new Set<string>();
    const add = (path: string) => {
      const segs = path.split("/");
      for (let i = 1; i <= segs.length; i++) {
        set.add(segs.slice(0, i).join("/"));
      }
    };
    for (const f of this.folders) add(f);
    for (const d of this.docs) if (d.folder) add(d.folder);
    return [...set].sort((a, b) => a.localeCompare(b));
  }

  /** Documents directly in `path` (recursive=false) or in `path` and any of its
   *  descendants (recursive=true). `path === null` means the root level. */
  docsInFolder(path: string | null, recursive = true): StoredDocument[] {
    if (path === null) {
      return this.docs.filter((d) => !d.folder);
    }
    const prefix = path + "/";
    return this.docs.filter(
      (d) =>
        d.folder === path || (recursive && d.folder?.startsWith(prefix)),
    );
  }

  async createFolder(path: string): Promise<void> {
    const norm = this.normalizeFolderPath(path);
    if (!norm) return;
    if (!this.folders.includes(norm)) {
      this.folders = [...this.folders, norm];
      await this.persistFolders();
    }
  }

  /** Delete a folder, all its descendant folders, AND every document they
   *  contain. The documents are removed for good. */
  async deleteFolder(path: string): Promise<void> {
    const norm = this.normalizeFolderPath(path);
    if (!norm) return;
    const prefix = norm + "/";
    const ids = this.docsInFolder(norm, true).map((d) => d.id);
    for (const id of ids) {
      await documents.deleteDocument(id);
    }
    this.folders = this.folders.filter(
      (f) => f !== norm && !f.startsWith(prefix),
    );
    this.selectedFolders = this.selectedFolders.filter(
      (f) => f !== norm && !f.startsWith(prefix),
    );
    await this.persistFolders();
    if (ids.length > 0) {
      const drop = new Set(ids);
      this.activeDocIds = this.activeDocIds.filter((id) => !drop.has(id));
      void this.syncConversationDocs();
      await this.refreshDocs();
    }
  }

  /** Rename a folder (and re-parent its descendants and documents). */
  async renameFolder(oldPath: string, newPath: string): Promise<void> {
    const from = this.normalizeFolderPath(oldPath);
    const to = this.normalizeFolderPath(newPath);
    if (!from || !to || from === to) return;
    const fromPrefix = from + "/";
    const remap = (p: string): string =>
      p === from ? to : p.startsWith(fromPrefix) ? to + "/" + p.slice(fromPrefix.length) : p;
    this.folders = [...new Set(this.folders.map(remap))];
    this.selectedFolders = [...new Set(this.selectedFolders.map(remap))];
    await this.persistFolders();
    // Re-parent affected documents, grouped by their new folder path.
    const affected = this.docsInFolder(from, true);
    const byNewFolder = new Map<string, string[]>();
    for (const d of affected) {
      const next = remap(d.folder as string);
      const list = byNewFolder.get(next) ?? [];
      list.push(d.id);
      byNewFolder.set(next, list);
    }
    for (const [folder, ids] of byNewFolder) {
      await documents.setDocumentFolder(ids, folder);
    }
    if (affected.length > 0) await this.refreshDocs();
  }

  async moveDocsToFolder(docIds: string[], folder: string | null): Promise<void> {
    if (docIds.length === 0) return;
    const norm = folder === null ? null : this.normalizeFolderPath(folder);
    if (norm) await this.createFolder(norm);
    await documents.setDocumentFolder(docIds, norm);
    await this.refreshDocs();
  }

  /** Toggle a folder's header checkbox: selects/deselects the folder (for the
   *  bulk bar, including empty folders) and activates/deactivates all of its
   *  documents (recursive). A folder counts as "on" when it is explicitly
   *  selected, or when every document it holds is already active. */
  toggleActiveFolder(path: string): void {
    const ids = this.docsInFolder(path, true).map((d) => d.id);
    const active = new Set(this.activeDocIds);
    const isOn =
      this.selectedFolders.includes(path) ||
      (ids.length > 0 && ids.every((id) => active.has(id)));
    if (isOn) {
      this.selectedFolders = this.selectedFolders.filter((p) => p !== path);
      if (ids.length > 0) {
        const drop = new Set(ids);
        this.activeDocIds = this.activeDocIds.filter((id) => !drop.has(id));
      }
    } else {
      this.selectedFolders = [...this.selectedFolders, path];
      for (const id of ids) active.add(id);
      this.activeDocIds = [...active];
    }
    void this.syncConversationDocs();
  }
}

export const app = new AppState();
